import React, { useContext, useState } from "react";
import {
    Disclosure,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    Dialog,
    Transition
  } from "@headlessui/react";
  import {
    Bars3Icon,
    XMarkIcon,
    HomeIcon,
    MapPinIcon,
    ClipboardDocumentCheckIcon,
    Cog6ToothIcon,
    ArrowRightOnRectangleIcon,
    ExclamationTriangleIcon
  } from "@heroicons/react/24/outline";
  import { Link, useNavigate, useLocation } from "react-router-dom";
  import { AuthContext } from "../context/AuthContext";
  
  const navigation = [
    { name: "Dashboard", href: "/", icon: HomeIcon },
    { name: "Choose Locations", href: "/choose-locations", icon: MapPinIcon },
    { name: "PatrolPlan", href: "/patrolplan", icon: ClipboardDocumentCheckIcon },
    { name: "Settings", href: "/settings", icon: Cog6ToothIcon },
  ];
  
  function classNames(...classes) {
    return classes.filter(Boolean).join(" ");
  }
  
  export default function Sidebar({ children }) {
    const { user, logout } = useContext(AuthContext);
    const navigate = useNavigate();
    const location = useLocation();
    const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
    const initials = `${user?.first_name?.[0] || ""}${user?.last_name?.[0] || ""}`.toUpperCase();
  
    const handleLogout = () => {
      // Clear all relevant cache items
      const keysToRemove = [
        'wardenLastLocation',
        'userAreas',
        'currentPeriod',
        'selectedArea',
        'lastSelectedAreas',
        'selectedAreas',
        'lastLocation',
        'lastUserLocation',
        'routeCache',
        'cachedSites',
        'hasVisitedPatrolPlan',
        'filteredSites',
        'errorLocation',
        'mapboxEnabled'
      ];

      keysToRemove.forEach(key => {
        try {
          localStorage.removeItem(key);
          console.log(`Cleared ${key} from localStorage`);
        } catch (e) {
          console.error(`Error clearing ${key}:`, e);
        }
      });

      console.log("Cleared all data from localStorage");
      logout();
      navigate("/login");
    };
  
    return (
      <div className="flex flex-col md:flex-row min-h-screen bg-customIndigo text-white">
        {/* Logout Confirmation Modal */}
        <Transition appear show={isLogoutModalOpen} as={React.Fragment}>
          <Dialog as="div" className="relative z-50" onClose={() => setIsLogoutModalOpen(false)}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/25" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4 text-center">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                    <div className="flex items-center gap-3">
                      <div className="flex-shrink-0">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-500" aria-hidden="true" />
                      </div>
                      <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                        Confirm Logout
                      </Dialog.Title>
                    </div>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to log out? This will clear all your current session data.
                      </p>
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-customIndigo focus-visible:ring-offset-2"
                        onClick={() => setIsLogoutModalOpen(false)}
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        className="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                        onClick={() => {
                          setIsLogoutModalOpen(false);
                          handleLogout();
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
  
        {/* Sidebar (Desktop) */}
        <div className="hidden md:flex w-64 flex-col justify-between bg-gradient-to-b from-[oklch(0.55_0.1041_243.43)] to-[oklch(0.65_0.1041_243.43)] shadow-lg">
          <div>
            <div className="p-6 text-2xl font-bold">PatrolManager</div>
            <nav className="space-y-1 px-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={classNames(
                      location.pathname === item.href
                        ? "bg-white text-customIndigo font-semibold"
                        : "hover:bg-white/10",
                      "flex items-center gap-2 rounded-md px-3 py-2 text-sm transition"
                    )}
                  >
                    <Icon className="h-5 w-5" />
                    {item.name}
                  </Link>
                );
              })}
              <button
                onClick={() => setIsLogoutModalOpen(true)}
                className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-red-300 hover:bg-red-500/20 hover:text-red-200 transition w-full"
              >
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
                Log out
              </button>
            </nav>
          </div>
  
          <div className="p-4 border-t border-white/20">
            <Menu as="div" className="relative">
              <MenuButton className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-medium text-customIndigo">
                {initials || "U"}
              </MenuButton>
              <MenuItems className="absolute bottom-12 left-0 mt-2 w-48 origin-top-left rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                <MenuItem>
                  {({ active }) => (
                    <Link
                      to="/settings"
                      className={classNames(
                        active ? "bg-gray-100" : "",
                        "block px-4 py-2 text-sm text-gray-700"
                      )}
                    >
                      Settings
                    </Link>
                  )}
                </MenuItem>
                <MenuItem>
                  {({ active }) => (
                    <button
                      onClick={() => setIsLogoutModalOpen(true)}
                      className={classNames(
                        active ? "bg-gray-100" : "",
                        "block w-full text-left px-4 py-2 text-sm text-red-600"
                      )}
                    >
                      Log out
                    </button>
                  )}
                </MenuItem>
              </MenuItems>
            </Menu>
          </div>
        </div>
  
        {/* Mobile Nav */}
        <div className="md:hidden w-full">
          <Disclosure as="nav" className="bg-customIndigo text-white shadow">
            {({ open }) => (
              <>
                <div className="relative flex h-16 items-center px-4 sm:px-6">
                  <Disclosure.Button className="absolute left-4 inline-flex items-center justify-center rounded-md p-2 text-white hover:bg-white/20 hover:text-white">
                    {open ? (
                      <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                    ) : (
                      <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                    )}
                  </Disclosure.Button>
  
                  <div className="flex-1 text-center">
                    <span className="text-xl font-bold">PatrolManager</span>
                  </div>
  
                  <div className="absolute right-4 flex items-center">
                    <Menu as="div" className="relative">
                      <MenuButton className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-sm font-medium text-customIndigo">
                        {initials || "U"}
                      </MenuButton>
                      <MenuItems className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                        <MenuItem>
                          {({ active }) => (
                            <Link
                              to="/settings"
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block px-4 py-2 text-sm text-gray-700"
                              )}
                            >
                              Settings
                            </Link>
                          )}
                        </MenuItem>
                        <MenuItem>
                          {({ active }) => (
                            <button
                              onClick={() => setIsLogoutModalOpen(true)}
                              className={classNames(
                                active ? "bg-gray-100" : "",
                                "block w-full text-left px-4 py-2 text-sm text-red-600"
                              )}
                            >
                              Log out
                            </button>
                          )}
                        </MenuItem>
                      </MenuItems>
                    </Menu>
                  </div>
                </div>
  
                <Disclosure.Panel className="space-y-1 px-4 pb-3 pt-2 bg-gray-100 text-gray-800">
                  {navigation.map((item) => {
                    const Icon = item.icon;
                    return (
                      <Disclosure.Button
                        key={item.name}
                        as={Link}
                        to={item.href}
                        className="flex items-center gap-2 rounded-md px-3 py-2 text-base font-medium hover:bg-gray-100 hover:text-customIndigo"
                      >
                        <Icon className="h-5 w-5" />
                        {item.name}
                      </Disclosure.Button>
                    );
                  })}
                  <button
                    onClick={() => setIsLogoutModalOpen(true)}
                    className="mt-2 block w-full text-left rounded-md px-3 py-2 text-base font-medium text-red-600 hover:bg-gray-100"
                  >
                    Log out
                  </button>
                </Disclosure.Panel>
              </>
            )}
          </Disclosure>
        </div>
  
        {/* Page content */}
        <main className="flex-grow bg-white p-4 md:p-6 overflow-x-auto">
          {children}
        </main>
      </div>
    );
  }
  